/**
 * Usage Management Component
 * 
 * Shows consumption bars for products and features when org is not on unlimited plan.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Package, Layers, AlertTriangle } from 'lucide-react';
import { useSafeMode } from '../contexts/SafeModeContext';
import { billingApi } from '../services/api';
import { cn } from '../lib/utils';

const UsageManagement = ({ refreshKey }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    fetchUsage();
  }, [refreshKey]);

  const fetchUsage = async () => {
    try {
      setLoading(true);
      const response = await billingApi.getContext();
      setUsage(response.data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`.replace('.0M', 'M');
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`.replace('.0K', 'K');
    return num?.toString() || '0';
  };

  const getProgressColor = (used, limit) => {
    if (!limit) return 'bg-emerald-500';
    const percent = (used / limit) * 100;
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getTextColor = (used, limit) => {
    if (!limit) return 'text-emerald-500';
    const percent = (used / limit) * 100;
    if (percent >= 90) return 'text-red-500';
    if (percent >= 75) return 'text-amber-500';
    return 'text-emerald-500';
  };

  if (loading) {
    return (
      <Card className={cn("animate-pulse", colors.card)}>
        <CardHeader className="pb-3">
          <div className={cn("h-6 w-48 rounded", colors.cardMuted)} />
        </CardHeader>
        <CardContent>
          <div className={cn("h-24 rounded", colors.cardMuted)} />
        </CardContent>
      </Card>
    );
  }

  // Don't show if unlimited products AND features
  const hasProductLimit = usage?.products?.limit !== null && usage?.products?.limit !== undefined;
  const hasFeatureLimit = usage?.features?.limit !== null && usage?.features?.limit !== undefined;
  
  if (!hasProductLimit && !hasFeatureLimit) {
    return null; // Unlimited plan, don't show usage bars
  }

  const productsUsed = usage?.products?.used || 0;
  const productsLimit = usage?.products?.limit;
  const productsPercent = productsLimit ? Math.min(100, (productsUsed / productsLimit) * 100) : 0;

  const featuresUsed = usage?.features?.used || 0;
  const featuresLimit = usage?.features?.limit;
  const featuresPercent = featuresLimit ? Math.min(100, (featuresUsed / featuresLimit) * 100) : 0;

  return (
    <Card className={cn(colors.card)}>
      <CardHeader className="pb-3">
        <CardTitle className={cn("text-lg flex items-center gap-2", colors.text)}>
          <Layers className="w-5 h-5" />
          Plan Usage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Products Usage */}
        {hasProductLimit && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className={cn("w-4 h-4", colors.textMuted)} />
                <span className={cn("text-sm font-medium", colors.text)}>Products</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-semibold", getTextColor(productsUsed, productsLimit))}>
                  {productsUsed}
                </span>
                <span className={cn("text-xs", colors.textMuted)}>
                  / {formatNumber(productsLimit)}
                </span>
              </div>
            </div>
            <Progress 
              value={productsPercent} 
              className={cn("h-2", colors.cardMuted)}
              indicatorClassName={getProgressColor(productsUsed, productsLimit)}
            />
            {productsPercent >= 90 && (
              <div className="flex items-center gap-1 text-xs text-amber-500">
                <AlertTriangle className="w-3 h-3" />
                <span>Approaching limit - upgrade for more products</span>
              </div>
            )}
          </div>
        )}

        {/* Features Usage */}
        {hasFeatureLimit && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className={cn("w-4 h-4", colors.textMuted)} />
                <span className={cn("text-sm font-medium", colors.text)}>Features</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn("text-sm font-semibold", getTextColor(featuresUsed, featuresLimit))}>
                  {featuresUsed}
                </span>
                <span className={cn("text-xs", colors.textMuted)}>
                  / {formatNumber(featuresLimit)}
                </span>
              </div>
            </div>
            <Progress 
              value={featuresPercent} 
              className={cn("h-2", colors.cardMuted)}
              indicatorClassName={getProgressColor(featuresUsed, featuresLimit)}
            />
            {featuresPercent >= 90 && (
              <div className="flex items-center gap-1 text-xs text-amber-500">
                <AlertTriangle className="w-3 h-3" />
                <span>Approaching limit - upgrade for more features</span>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        <div className={cn("text-xs pt-2 border-t", colors.textMuted, colors.border)}>
          {hasProductLimit && hasFeatureLimit ? (
            <span>
              {usage?.products?.remaining || 0} products and {usage?.features?.remaining || 0} features remaining
            </span>
          ) : hasProductLimit ? (
            <span>{usage?.products?.remaining || 0} products remaining</span>
          ) : (
            <span>{usage?.features?.remaining || 0} features remaining</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageManagement;
